import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameName, GAME_LIMITS, STORAGE_KEYS } from '../constants.ts';
import api from '../utils/api.ts';
import './CreateGame.css';

function CreateGame() {
  const [gameType, setGameType] = useState<GameName>(GameName.Gotcha);
  const [roomName, setRoomName] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(10);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const [hasToken, setHasToken] = useState(!!localStorage.getItem(STORAGE_KEYS.JWT_TOKEN));
  const [guestAlias, setGuestAlias] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const maxLimit = GAME_LIMITS[gameType];
    if (maxPlayers > maxLimit) {
      setError(`${gameType} has a maximum of ${maxLimit} players`);
      return;
    }

    setSubmitting(true);

    if (!hasToken) {
      const alias = guestAlias.trim();
      if (alias.length < 2) {
        setError('Please enter your name (at least 2 characters)');
        setSubmitting(false);
        return;
      }
      const guestResult = await api.guestLogin(alias);
      if (!guestResult.success) {
        setError(guestResult.error || 'Failed to start guest session');
        setSubmitting(false);
        return;
      }
      setHasToken(true);
    }

    const adminName = localStorage.getItem(STORAGE_KEYS.USERNAME) || 'Admin';
    const result = await api.createRoom(gameType, roomName, maxPlayers, adminName);
    setSubmitting(false);

    if (result.success) {
      navigate(`/game/${result.data.roomId}`);
    } else {
      setError(result.error || 'Failed to create room');
    }
  };

  return (
    <div className="create-game">
      <h2>🎲 Create New Game</h2>

      <form onSubmit={handleSubmit} className="game-form">
        {!hasToken && (
          <div className="form-group">
            <label htmlFor="guestAlias">Your Name</label>
            <input
              id="guestAlias"
              type="text"
              value={guestAlias}
              onChange={(e) => setGuestAlias(e.target.value)}
              placeholder="e.g. Anna"
              maxLength={20}
              required
            />
            <div className="game-info">
              No account needed — your name is shown to other players in the room.
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="gameType">Game Type</label>
          <select
            id="gameType"
            value={gameType}
            onChange={(e) => setGameType(e.target.value as GameName)}
          >
            <option value={GameName.Gotcha}>🎯 Gotcha</option>
            <option value={GameName.GuessThePerson}>🤔 Guess The Person</option>
            <option value={GameName.GenderEqualityCuisine}>👩‍🍳 Gender Equality Cuisine</option>
          </select>
          <div className="game-info">
            Max {GAME_LIMITS[gameType]} players
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="roomName">Room Name</label>
          <input
            id="roomName"
            type="text"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            placeholder="Enter a fun room name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="maxPlayers">Number of Players</label>
          <input
            id="maxPlayers"
            type="number"
            value={maxPlayers}
            onChange={(e) => setMaxPlayers(parseInt(e.target.value) || 2)}
            min={2}
            max={GAME_LIMITS[gameType]}
            required
          />
        </div>

        <button type="submit" className="submit-btn" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create Room'}
        </button>

        {error && <div className="error-message">{error}</div>}
      </form>
    </div>
  );
}

export default CreateGame;
